import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, updateUser } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tier, setTier] = useState(null);
  const [addon, setAddon] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payment/stripe-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data.payment_status === "paid") {
          setStatus("success");
          setTier(data.tier);
          if (data.addon) setAddon(data.addon);
          if (data.tier) updateUser({ tier: data.tier });
          else if (data.addon) updateUser({ active_addon: data.addon });
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    let interval;
    const startPolling = async () => {
      const done = await poll();
      if (done) return;
      let count = 0;
      interval = setInterval(async () => {
        count++;
        setPollCount(count);
        const done = await poll();
        if (done || count >= 15) {
          clearInterval(interval);
          if (!done) setStatus("timeout");
        }
      }, 2000);
    };
    startPolling();
    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md text-center space-y-6">
        {status === "checking" && (
          <div className="space-y-4 animate-in fade-in">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h1 className="font-heading text-2xl font-medium">Confirming your payment...</h1>
            <p className="text-muted-foreground text-sm">This usually takes a few seconds.</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6 animate-in fade-in zoom-in-95">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-medium mb-2">Welcome aboard!</h1>
              <p className="text-muted-foreground">
                {addon ? "Your add-on has been activated successfully." : "Your subscription has been activated successfully."}
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-2xl">
              <p className="text-sm font-medium">{addon ? "Your add-on is now active" : "Your plan is now active"}</p>
              {tier && <p className="text-xs text-muted-foreground mt-1">Tier: {tier.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</p>}
              {addon && <p className="text-xs text-muted-foreground mt-1" data-testid="payment-success-addon">Add-on: {addon.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>}
            </div>
            <Button
              data-testid="payment-success-continue-btn"
              onClick={() => navigate("/chat")}
              className="w-full btn-omni bg-primary text-primary-foreground gap-2 rounded-full"
            >
              Start chatting <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {status === "timeout" && (
          <div className="space-y-6 animate-in fade-in">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="font-heading text-2xl font-medium">Payment is processing</h1>
            <p className="text-muted-foreground text-sm">
              Your payment may still be processing. If your plan doesn&apos;t activate within a few minutes, please contact support.
            </p>
            <Button onClick={() => navigate("/chat")} className="w-full rounded-full">
              Go to Chat
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6 animate-in fade-in">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="font-heading text-2xl font-medium">Something went wrong</h1>
            <p className="text-muted-foreground text-sm">We couldn&apos;t find your payment session.</p>
            <Button onClick={() => navigate("/pricing")} className="w-full rounded-full">
              Back to Pricing
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
